import invariant from "invariant";

import type { LangObj, MultiLangObj } from "@ms/gatsby-site/src/types/types";
import type { TranslatedError } from "@theme/types/global";

/**
 * Helpers - Here is where we throw in helpers that don't fit into a specific category.
 * Please add into this file sparingly.
 */

/**
 * Wait Till True
 *
 * To prevent race conditions we sometimes need to check until one function is true
 * to run another function
 * if the first function returns true, run callback function,
 * else set timeout and try again
 * exponentially increasing the timeout delay
 * after 5 retries, give up
 *
 * Note: delay defaults to 100 and increases exponentially;
 *
 * @param {() => boolean} boolFunc
 * @param {() => void} callback
 * @param {Number} delay
 */
export const waitTillTrue = (
  boolFunc: () => boolean,
  callback: () => void,
  fallback?: () => void,
) => {
  let retryCount = 1;
  let delay = 100;
  const scheduler = () => {
    if (boolFunc() === true) {
      callback();
    } else if (retryCount <= 5) {
      setTimeout(() => {
        scheduler();
      }, delay);
      delay *= 2;
      retryCount += 1;
    } else {
      fallback && fallback();
    }
  };
  scheduler();
};

/**
 * Convert Object of LangObjs to One Locale
 *
 * Let's say you had an object where each key had an array of LangObjs
 * that looked like this:
 *
 * const obj = {
 *    required: [
 *      { lang: "en", value: "Required" },
 *      { lang: "de", value: "Pflichtfeld" },
 *    ],
 * }
 *
 * This function would return this if you pass in "de" as the language:
 * {
 *    required: "Pflichtfeld"
 * }
 *
 * @param {MultiLangObj} multiLangObj
 * @param {string} language
 * @return { [key: string]: string }
 */
export const convertMultiLangObjToOneLocale = (multiLangObj: MultiLangObj, language: string) => {
  const obj: { [key: string]: string } = {};

  for (const key in multiLangObj) {
    const value = multiLangObj[key].find((n: LangObj) => n.lang === language)?.value;

    invariant(value, `${key} is required in language: ${language}`);

    obj[key] = value;
  }

  return obj;
};

/**
 * Get Translated Error Message
 *
 * Pass an error message and an error token and get the translated error message
 *
 * @param message
 * @param token
 */
export const getTranslatedError = (message: string, token: string): TranslatedError => {
  const translatedError: TranslatedError = new Error(message);
  translatedError.token = token;

  return translatedError;
};
